import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api, { tournaments as tournamentsApi, tournamentAdmin, tournamentPush } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Trophy, Trash2, Edit, Calendar, Users, Plus, Download, UserPlus, UserMinus,
  Clock, CheckCircle, ListFilter, AlertTriangle, Swords, CreditCard, Share2, Copy,
  PlayCircle, Archive, MapPin, Image as ImageIcon, Bell,
} from 'lucide-react';
import { GAMES, FORMATS, getGameColor, getGameName } from '../constants/theme';
import ShareEvent from '../components/ShareEvent';
import BannerEditor from '../components/BannerEditor';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

// Fast-appearing tooltip for action buttons
const ActionTip = ({ label, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent className="bg-[#252525] border-[#333] text-white text-xs">{label}</TooltipContent>
  </Tooltip>
);

const PAYMENT_METHODS = [
  { value: 'en_attente', label: 'En attente' },
  { value: 'cb_sur_place', label: 'CB sur place' },
  { value: 'arcanapp', label: 'Arcanapp' },
  { value: 'espece', label: 'Espèce' },
  { value: 'bon_achat', label: "Bon d'achat" },
  { value: 'cheque', label: 'Chèque' },
  { value: 'virement', label: 'Virement' },
  { value: 'offert', label: 'Offert' },
];
const paymentLabel = (v) => (PAYMENT_METHODS.find((p) => p.value === v) || {}).label || 'En attente';
const paymentBadgeClass = (v) => {
  if (v === 'en_attente') return 'bg-amber-500/15 text-amber-400';
  if (v === 'offert') return 'bg-[#7B68EE]/15 text-[#7B68EE]';
  return 'bg-green-500/15 text-green-400';
};

export default function ShopTournamentsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const normalizeTab = (t) => {
    if (t === 'closed' || t === 'history' || t === 'archives') return 'archives';
    if (['active', 'registered', 'ongoing', 'toclose'].includes(t)) return t;
    return 'active';
  };

  const [tab, setTab] = useState(() => normalizeTab(searchParams.get('tab')));
  const [tournaments, setTournaments] = useState([]);
  const [registered, setRegistered] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sync tab when the URL query changes (e.g. navigating from the nav menu)
  useEffect(() => {
    setTab(normalizeTab(searchParams.get('tab')));
  }, [searchParams]);

  const changeTab = (t) => {
    setTab(t);
    setSearchParams({ tab: t }, { replace: true });
  };

  // Dialog states
  const [dialogType, setDialogType] = useState(null);
  const [selected, setSelected] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [waitlistData, setWaitlistData] = useState({ interested: [], waitlist: [] });
  const [addPlayerSearch, setAddPlayerSearch] = useState('');
  const [manualPlayer, setManualPlayer] = useState({ first_name: '', last_name: '', email: '', phone: '', payment_method: 'en_attente' });
  const [manualWaitlist, setManualWaitlist] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [editData, setEditData] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [editBannerBlob, setEditBannerBlob] = useState(null);
  const [showBannerEditor, setShowBannerEditor] = useState(false);
  // Remove player confirm
  const [removeTarget, setRemoveTarget] = useState(null);

  const loadTournaments = useCallback(async () => {
    setLoading(true);
    try {
      const isAdminRole = user?.role === 'ADMIN' || user?.role === 'EDITOR';
      const params = isAdminRole ? { limit: 200 } : { organizer_id: user?.id, limit: 200 };
      const [orgRes, regRes] = await Promise.all([
        tournamentsApi.list(params),
        api.get('/users/me/tournaments').catch(() => ({ data: {} })),
      ]);
      const all = orgRes.data.tournaments || orgRes.data || [];
      setTournaments(all);
      setRegistered(regRes.data?.registered || []);
    } catch {
      toast.error('Erreur chargement des tournois');
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.role]);

  useEffect(() => { loadTournaments(); }, [loadTournaments]);

  // Deep-link: ouvrir l'édition d'un tournoi via ?edit=<id> (depuis le dashboard)
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && tournaments.length) {
      const t = tournaments.find((x) => x.id === editId);
      if (t) {
        openEdit(t);
        const sp = new URLSearchParams(searchParams);
        sp.delete('edit');
        setSearchParams(sp, { replace: true });
      }
    }
  }, [searchParams, tournaments]);

  const now = new Date();
  const statusOf = (t) => t.status || 'upcoming';
  const isOverdue = (t) => t.date && new Date(t.date) < now && statusOf(t) === 'upcoming';

  // Tournaments organized by the shop, grouped by lifecycle
  const [pushDialog, setPushDialog] = useState(null); // { tournament, quota }
  const [pushLoading, setPushLoading] = useState(false);

  const openPush = async (t) => {
    setPushLoading(true);
    setPushDialog({ tournament: t, quota: null });
    try {
      const r = await tournamentPush.quota(t.id);
      setPushDialog({ tournament: t, quota: r.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement quota');
      setPushDialog(null);
    } finally {
      setPushLoading(false);
    }
  };

  const confirmPush = async () => {
    if (!pushDialog?.tournament) return;
    setPushLoading(true);
    try {
      const r = await tournamentPush.send(pushDialog.tournament.id);
      if (r.data.payment_required && r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }
      toast.success(`Notification envoyée à ${r.data.recipients} joueur(s) !`);
      setPushDialog(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'envoyer la notification");
    } finally {
      setPushLoading(false);
    }
  };

  const activeTournaments = tournaments.filter(t => statusOf(t) === 'upcoming' && !isOverdue(t));
  const ongoingTournaments = tournaments.filter(t => statusOf(t) === 'in_progress');
  const toCloseTournaments = tournaments.filter(t => statusOf(t) === 'completed' || isOverdue(t));
  const archivedTournaments = tournaments.filter(t => statusOf(t) === 'closed' || statusOf(t) === 'cancelled');

  const closeDialog = () => {
    setDialogType(null);
    setSelected(null);
    setParticipants([]);
    setWaitlistData({ interested: [], waitlist: [] });
    setAddPlayerSearch('');
    setManualPlayer({ first_name: '', last_name: '', email: '', phone: '', payment_method: 'en_attente' });
    setManualWaitlist({ first_name: '', last_name: '', email: '', phone: '' });
    setEditData({});
    setRemoveTarget(null);
  };

  // === OPEN DIALOGS ===
  const openPlayers = async (t) => {
    setSelected(t);
    setDialogType('players');
    try {
      const res = await tournamentAdmin.getParticipants(t.id);
      setParticipants(res.data);
    } catch { setParticipants([]); }
  };

  const openWaitlist = async (t) => {
    setSelected(t);
    setDialogType('waitlist');
    try {
      const res = await tournamentAdmin.getWaitlist(t.id);
      setWaitlistData(res.data);
    } catch { setWaitlistData({ interested: [], waitlist: [] }); }
  };

  const openEdit = (t) => {
    setSelected(t);
    const venue = t.venue || {};
    setEditData({
      name: t.name || '',
      game: t.game || '',
      format: t.format || '',
      description: t.description || '',
      date: t.date ? t.date.slice(0, 16) : '',
      end_date: t.end_date ? t.end_date.slice(0, 16) : '',
      registration_deadline: t.registration_deadline ? t.registration_deadline.slice(0, 16) : '',
      price: t.price ?? 0,
      capacity: t.capacity ?? 32,
      venue_name: venue.name || '',
      venue_address: venue.address || '',
      venue_city: venue.city || '',
      is_recurring: t.is_recurring || false,
      recurrence_rule: t.recurrence_rule || '',
      banner_url: t.banner_url || '',
    });
    setEditBannerBlob(null);
    setShowBannerEditor(false);
    setDialogType('edit');
  };

  const openDelete = async (t) => {
    setSelected(t);
    setDialogType('delete');
    setParticipants([]);
    try {
      const res = await tournamentAdmin.getParticipants(t.id);
      setParticipants(res.data || []);
    } catch { setParticipants([]); }
  };
  const openClose = (t) => { setSelected(t); setDialogType('close'); };

  // === ACTIONS ===
  const handleAddPlayer = async () => {
    if (!manualPlayer.first_name.trim() || !manualPlayer.last_name.trim()) {
      toast.error('Nom et prénom obligatoires');
      return;
    }
    if (!manualPlayer.payment_method) {
      toast.error('Moyen de paiement obligatoire');
      return;
    }
    setActionLoading(true);
    try {
      await tournamentAdmin.addPlayerManual(selected.id, {
        first_name: manualPlayer.first_name.trim(),
        last_name: manualPlayer.last_name.trim(),
        email: manualPlayer.email.trim(),
        phone: manualPlayer.phone.trim(),
        payment_method: manualPlayer.payment_method,
      });
      toast.success('Joueur ajouté');
      setManualPlayer({ first_name: '', last_name: '', email: '', phone: '', payment_method: 'en_attente' });
      const res = await tournamentAdmin.getParticipants(selected.id);
      setParticipants(res.data);
      loadTournaments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setActionLoading(false); }
  };

  const handleChangePayment = async (playerId, method) => {
    setParticipants((prev) => prev.map((p) => (p.id === playerId ? { ...p, payment_method: method } : p)));
    try {
      await tournamentAdmin.updatePayment(selected.id, playerId, method);
      toast.success('Moyen de paiement mis à jour');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
      const res = await tournamentAdmin.getParticipants(selected.id);
      setParticipants(res.data);
    }
  };

  const confirmRemovePlayer = (player) => {
    setRemoveTarget(player);
    setDialogType('remove-player');
  };

  const handleRemovePlayer = async () => {
    if (!removeTarget) return;
    setActionLoading(true);
    try {
      await tournamentAdmin.removePlayer(selected.id, removeTarget.id);
      toast.success('Joueur retiré');
      setRemoveTarget(null);
      setDialogType('players');
      const res = await tournamentAdmin.getParticipants(selected.id);
      setParticipants(res.data);
      loadTournaments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setActionLoading(false); }
  };

  const handleAddWaitlist = async () => {
    const w = manualWaitlist;
    if (!w.first_name.trim() || !w.last_name.trim() || !w.email.trim() || !w.phone.trim()) {
      toast.error('Nom, prénom, email et téléphone obligatoires');
      return;
    }
    setActionLoading(true);
    try {
      await tournamentAdmin.addToWaitlist(selected.id, {
        first_name: w.first_name.trim(), last_name: w.last_name.trim(),
        email: w.email.trim(), phone: w.phone.trim(),
      });
      toast.success("Ajouté à la liste d'attente (notification envoyée)");
      setManualWaitlist({ first_name: '', last_name: '', email: '', phone: '' });
      const res = await tournamentAdmin.getWaitlist(selected.id);
      setWaitlistData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setActionLoading(false); }
  };

  const handleRelanceWaitlist = async () => {
    setActionLoading(true);
    try {
      const res = await tournamentAdmin.relanceWaitlist(selected.id);
      toast.success(res.data.message + (res.data.email_sent ? ' (email envoyé)' : ''));
      const w = await tournamentAdmin.getWaitlist(selected.id);
      setWaitlistData(w.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setActionLoading(false); }
  };

  const handleUpdate = async () => {
    if (!editData.name?.trim()) { toast.error('Le nom est obligatoire'); return; }
    if (!editData.game) { toast.error('Le jeu est obligatoire'); return; }
    if (!editData.format) { toast.error('Le format est obligatoire'); return; }
    if (!editData.date) { toast.error('La date est obligatoire'); return; }
    const currentPlayers = (selected?.participants || []).length;
    if (editData.capacity < currentPlayers) {
      toast.error(`Impossible: ${currentPlayers} joueurs déjà inscrits (minimum: ${currentPlayers})`);
      return;
    }
    setActionLoading(true);
    try {
      // Upload a newly generated/modified banner if the editor was used
      let bannerUrl = editData.banner_url || null;
      if (showBannerEditor && editBannerBlob) {
        try {
          const fd = new FormData();
          fd.append('file', editBannerBlob, 'banner.jpg');
          const up = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/shop-asset`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
            body: fd,
          });
          if (up.ok) { const j = await up.json(); bannerUrl = j.url; }
        } catch (e) { console.warn('Banner upload failed:', e); }
      }
      await tournamentsApi.update(selected.id, {
        name: editData.name,
        game: editData.game,
        format: editData.format,
        description: editData.description,
        date: editData.date ? new Date(editData.date).toISOString() : undefined,
        end_date: editData.end_date ? new Date(editData.end_date).toISOString() : null,
        registration_deadline: editData.registration_deadline ? new Date(editData.registration_deadline).toISOString() : null,
        price: parseFloat(editData.price) || 0,
        capacity: parseInt(editData.capacity) || 32,
        venue: { name: editData.venue_name, address: editData.venue_address, city: editData.venue_city },
        is_recurring: editData.is_recurring,
        recurrence_rule: editData.recurrence_rule || null,
        banner_url: bannerUrl,
      });
      toast.success('Tournoi mis à jour');
      closeDialog();
      loadTournaments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setActionLoading(false); }
  };

  const handleClose = async () => {
    setActionLoading(true);
    try {
      await tournamentAdmin.closeTournament(selected.id);
      toast.success('Tournoi clôturé');
      closeDialog();
      loadTournaments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setActionLoading(false); }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await tournamentsApi.delete(selected.id);
      toast.success('Tournoi supprimé. Les inscrits seront notifiés.');
      closeDialog();
      loadTournaments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setActionLoading(false); }
  };

  const handleDownloadCsv = async (t) => {
    try {
      const response = await tournamentAdmin.downloadCsv(t.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `participants_${t.name.replace(/ /g, '_')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV téléchargé');
    } catch { toast.error('Erreur téléchargement'); }
  };

  const handleDuplicate = (t) => {
    const venue = t.venue || {};
    const dupData = {
      name: t.name,
      game: t.game || '',
      format: t.format || '',
      customFormat: '',
      description: t.description || '',
      venue_name: venue.name || '',
      venue_address: venue.address || '',
      venue_city: venue.city || '',
      venue_postal: venue.postal || '',
      no_physical_venue: t.no_physical_venue || false,
      capacity: t.capacity ? String(t.capacity) : '',
      refund_type: t.refund_type || 'hours',
      refund_hours: t.refund_hours || 24,
      price: t.price ?? 0,
      waitlist_mode: t.waitlist_mode || 'manual',
      banner_url: t.banner_url || '',
      // Explicitly EXCLUDE: tournament_code, date, end_date
    };
    sessionStorage.setItem('duplicate_tournament', JSON.stringify(dupData));
    navigate('/create-tournament?duplicate=true');
    toast.info('Formulaire pré-rempli. Ajustez les dates et le code tournoi.');
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('fr-FR', {
      weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  };

  const editFormats = editData.game ? (FORMATS[editData.game] || []) : [];
  const TABS = [
    { key: 'active', label: 'Tournois actifs', icon: Trophy, count: activeTournaments.length },
    { key: 'registered', label: 'Inscrits', icon: CheckCircle, count: registered.length },
    { key: 'ongoing', label: 'En cours', icon: PlayCircle, count: ongoingTournaments.length },
    { key: 'toclose', label: 'À clôturer', icon: AlertTriangle, count: toCloseTournaments.length },
    { key: 'archives', label: 'Archives', icon: Archive, count: archivedTournaments.length },
  ];
  const isOrganizedTab = tab !== 'registered';
  const emptyMessages = {
    active: 'Aucun tournoi actif',
    registered: "Vous n'êtes inscrit à aucun tournoi",
    ongoing: 'Aucun tournoi en cours',
    toclose: 'Aucun tournoi à clôturer',
    archives: 'Aucun tournoi archivé',
  };
  const displayList = tab === 'active' ? activeTournaments
    : tab === 'registered' ? registered
    : tab === 'ongoing' ? ongoingTournaments
    : tab === 'toclose' ? toCloseTournaments
    : archivedTournaments;
  const currentPlayerCount = (selected?.participants || []).length;

  const shareUrl = (t) => `${process.env.REACT_APP_BACKEND_URL}/api/share/tournament/${t.id}`;
  const shareUrlDirect = (t) => `${window.location.origin}/e/tournament/${t.id}`;
  const shareText = (t) => `${t.name} - ${getGameName(t.game)} ${t.format ? `(${t.format})` : ''} ${t.date ? `le ${formatDate(t.date)}` : ''}`.trim();

  const shareLinks = (t) => ({
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText(t) + '\n' + shareUrl(t))}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl(t))}`,
    instagram: shareUrl(t), // copy to clipboard for IG
    discord: shareUrl(t), // copy for Discord
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white" data-testid="shop-tournaments-title">Mes évènements</h1>
          <p className="text-[#666] text-sm mt-1">{activeTournaments.length} actifs · {ongoingTournaments.length} en cours · {toCloseTournaments.length} à clôturer</p>
        </div>
        <Button onClick={() => navigate('/create-tournament')} className="bg-[#FF6B35] hover:bg-[#E55A25] gap-2" data-testid="create-tournament-btn">
          <Plus className="w-4 h-4" />Créer un évènement
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#1a1a1a] p-1 rounded-lg border border-[#333] w-full sm:w-fit overflow-x-auto">
        {TABS.map((tb) => {
          const Icon = tb.icon;
          return (
            <button key={tb.key} onClick={() => changeTab(tb.key)}
              className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${tab === tb.key ? 'bg-[#FF6B35] text-white' : 'text-[#999] hover:text-white hover:bg-[#252525]'}`}
              data-testid={`tab-${tb.key}`}>
              <Icon className="w-4 h-4 inline mr-1.5" />{tb.label}
              <span className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] px-1 ${tab === tb.key ? 'bg-white/20 text-white' : 'bg-[#333] text-[#999]'}`}>{tb.count}</span>
            </button>
          );
        })}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]" /></div>
      ) : displayList.length === 0 ? (
        <Card className="bg-[#1a1a1a] border-[#333]"><CardContent className="p-8 text-center"><Trophy className="w-12 h-12 text-[#333] mx-auto mb-3" /><p className="text-[#666]">{emptyMessages[tab]}</p></CardContent></Card>
      ) : tab === 'registered' ? (
        <div className="space-y-2">
          {displayList.map((t) => (
            <Link key={t.id} to={`/player/event/tournament/${t.id}`} className="flex items-center gap-3 bg-[#1a1a1a] rounded-lg px-4 py-3 border border-[#333] hover:border-[#4ECDC4]/40 transition-colors" data-testid={`registered-row-${t.id}`}>
              <div className="w-1 h-12 rounded-full shrink-0" style={{ backgroundColor: getGameColor(t.game) }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-semibold text-sm truncate max-w-[300px]">{t.name}</span>
                  <Badge className="text-[10px] px-1.5 py-0" style={{ backgroundColor: `${getGameColor(t.game)}20`, color: getGameColor(t.game) }}>{getGameName(t.game)}</Badge>
                  {t.status === 'in_progress' && <Badge className="bg-green-500/15 text-green-400 text-[10px] gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />En cours</Badge>}
                </div>
                <div className="flex items-center gap-3 text-xs text-[#666] mt-1">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(t.date)}</span>
                  {t.venue?.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{t.venue.city}</span>}
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{(t.participants || []).length}/{t.capacity || '?'}</span>
                </div>
              </div>
              <Badge className="bg-[#4ECDC4]/10 text-[#4ECDC4] text-[10px] gap-1 shrink-0"><CheckCircle className="w-3 h-3" />Inscrit</Badge>
            </Link>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {displayList.map((t) => {
            const overdue = isOverdue(t);
            return (
              <div key={t.id} className={`flex items-center gap-3 bg-[#1a1a1a] rounded-lg px-4 py-3 transition-colors ${overdue ? 'border-2 border-red-500/60 bg-red-500/5' : 'border border-[#333] hover:border-[#FF6B35]/30'}`} data-testid={`tournament-row-${t.id}`}>
                <div className="w-1 h-12 rounded-full shrink-0" style={{ backgroundColor: getGameColor(t.game) }} />
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/player/event/tournament/${t.id}`)} data-testid={`tournament-open-${t.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-semibold text-sm truncate max-w-[300px]">{t.name}</span>
                    <Badge className="text-[10px] px-1.5 py-0" style={{ backgroundColor: `${getGameColor(t.game)}20`, color: getGameColor(t.game) }}>{getGameName(t.game)}</Badge>
                    {t.format && <Badge className="bg-[#252525] text-[#888] text-[10px] px-1.5 py-0">{t.format}</Badge>}
                    {overdue && <Badge className="bg-red-500/20 text-red-400 text-[10px] gap-1"><AlertTriangle className="w-3 h-3" />En retard</Badge>}
                    {(() => { const p = t.publish_at ? new Date(t.publish_at) : null; return (p && p > now) ? <Badge className="bg-[#7B68EE]/15 text-[#7B68EE] text-[10px] gap-1" data-testid={`scheduled-${t.id}`}><Clock className="w-3 h-3" />Programmé {p.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</Badge> : null; })()}
                    {t.status === 'in_progress' && <Badge className="bg-green-500/15 text-green-400 text-[10px] gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />En cours</Badge>}
                    {t.status === 'completed' && <Badge className="bg-amber-500/15 text-amber-400 text-[10px] gap-1"><CheckCircle className="w-3 h-3" />À clôturer</Badge>}
                    {(t.status === 'closed' || t.status === 'cancelled') && <Badge className="bg-[#333] text-[#888] text-[10px]">{t.status === 'cancelled' ? 'Annulé' : 'Clôturé'}</Badge>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#666] mt-1">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(t.date)}</span>
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" />{(t.participants || []).length}/{t.capacity || '?'}</span>
                    {t.price > 0 && <span>{t.price}€</span>}
                  </div>
                </div>
                {isOrganizedTab && (
                  <TooltipProvider delayDuration={120}>
                  <div className="flex items-center gap-0.5 shrink-0">
                    {tab !== 'archives' && (
                      <ActionTip label="Clôturer">
                        <Button variant="ghost" size="sm" onClick={() => openClose(t)} className="text-green-400 hover:text-green-300 hover:bg-green-400/10 gap-1 text-xs h-8 px-2" data-testid={`close-${t.id}`}>
                          <CheckCircle className="w-3.5 h-3.5" /><span className="hidden sm:inline">Clôturer</span>
                        </Button>
                      </ActionTip>
                    )}
                    <ActionTip label="Modifier"><Button variant="ghost" size="icon" onClick={() => openEdit(t)} className="text-[#666] hover:text-[#FF6B35] hover:bg-[#FF6B35]/10 h-8 w-8" data-testid={`edit-${t.id}`}><Edit className="w-4 h-4" /></Button></ActionTip>
                    <ActionTip label="Dupliquer"><Button variant="ghost" size="icon" onClick={() => handleDuplicate(t)} className="text-[#666] hover:text-[#7B68EE] hover:bg-[#7B68EE]/10 h-8 w-8" data-testid={`duplicate-${t.id}`}><Copy className="w-4 h-4" /></Button></ActionTip>
                    <ActionTip label="Liste de joueurs"><Button variant="ghost" size="icon" onClick={() => openPlayers(t)} className="text-[#666] hover:text-[#4ECDC4] hover:bg-[#4ECDC4]/10 h-8 w-8" data-testid={`players-${t.id}`}><Users className="w-4 h-4" /></Button></ActionTip>
                    <ActionTip label="Liste d'attente"><Button variant="ghost" size="icon" onClick={() => openWaitlist(t)} className="text-[#666] hover:text-[#7B68EE] hover:bg-[#7B68EE]/10 h-8 w-8" data-testid={`waitlist-${t.id}`}><ListFilter className="w-4 h-4" /></Button></ActionTip>
                    <ActionTip label="Export CSV"><Button variant="ghost" size="icon" onClick={() => handleDownloadCsv(t)} className="text-[#666] hover:text-blue-400 hover:bg-blue-400/10 h-8 w-8 hidden sm:flex" data-testid={`csv-${t.id}`}><Download className="w-4 h-4" /></Button></ActionTip>
                    {/* Share (lien FB/WhatsApp/Discord + Story Instagram) */}
                    <ActionTip label="Partager"><span><ShareEvent t={t} triggerClassName="text-[#666] hover:text-[#4ECDC4] hover:bg-[#4ECDC4]/10 h-8 w-8" /></span></ActionTip>
                    {tab !== 'archives' && (
                      <ActionTip label="Pousser une notif"><Button variant="ghost" size="icon" onClick={() => openPush(t)} className="text-[#666] hover:text-[#FFB800] hover:bg-[#FFB800]/10 h-8 w-8" data-testid={`push-${t.id}`}><Bell className="w-4 h-4" /></Button></ActionTip>
                    )}
                    <ActionTip label="Rondes"><Button variant="ghost" size="icon" onClick={() => navigate(`/tournament/${t.id}/manage`)} className="text-[#666] hover:text-[#FF6B35] hover:bg-[#FF6B35]/10 h-8 w-8" data-testid={`manage-${t.id}`}><Swords className="w-4 h-4" /></Button></ActionTip>
                    <ActionTip label="Supprimer"><Button variant="ghost" size="icon" onClick={() => openDelete(t)} className="text-[#666] hover:text-red-400 hover:bg-red-400/10 h-8 w-8" data-testid={`delete-${t.id}`}><Trash2 className="w-4 h-4" /></Button></ActionTip>
                  </div>
                  </TooltipProvider>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ============ PLAYERS DIALOG ============ */}
      <Dialog open={dialogType === 'players'} onOpenChange={closeDialog}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] text-white max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Joueurs inscrits — {selected?.name}</DialogTitle>
            <DialogDescription className="text-[#666]">{participants.length} / {selected?.capacity || '?'} places</DialogDescription>
          </DialogHeader>
          {participants.length > 0 && (() => {
            const price = selected?.price || 0;
            const counts = {};
            participants.forEach((p) => { const m = p.payment_method || 'en_attente'; counts[m] = (counts[m] || 0) + 1; });
            const pending = counts['en_attente'] || 0;
            const paid = participants.length - pending;
            const collected = participants.reduce((s, p) => { const m = p.payment_method || 'en_attente'; return (m !== 'en_attente' && m !== 'offert') ? s + price : s; }, 0);
            return (
              <div className="rounded-lg border border-[#333] bg-[#151515] p-3 space-y-2.5" data-testid="payment-recap">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-green-400 font-semibold" data-testid="recap-paid">{paid} payé{paid > 1 ? 's' : ''}</span>
                    <span className="text-[#444]">·</span>
                    <span className="text-amber-400 font-semibold" data-testid="recap-pending">{pending} en attente</span>
                  </div>
                  <span className="text-white font-bold text-sm" data-testid="recap-total">Total encaissé : {collected}€</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {PAYMENT_METHODS.filter((pm) => counts[pm.value]).map((pm) => {
                    const c = counts[pm.value];
                    const amount = (pm.value !== 'en_attente' && pm.value !== 'offert') ? c * price : 0;
                    return (
                      <span key={pm.value} className={`text-[11px] px-2 py-0.5 rounded-full ${paymentBadgeClass(pm.value)}`} data-testid={`recap-method-${pm.value}`}>
                        {pm.label} : {c}{amount ? ` (${amount}€)` : ''}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          <div className="space-y-4">
            {/* Manual add form */}
            <div className="rounded-lg border border-[#333] bg-[#151515] p-3 space-y-3" data-testid="manual-add-player-form">
              <p className="text-xs font-semibold text-[#FF6B35] uppercase tracking-wide flex items-center gap-1.5"><UserPlus className="w-3.5 h-3.5" />Ajouter un joueur manuellement</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-[#666] mb-1 block">Prénom *</label>
                  <Input placeholder="Prénom" value={manualPlayer.first_name} onChange={(e) => setManualPlayer({ ...manualPlayer, first_name: e.target.value })} className="bg-[#252525] border-[#333] text-white h-9" data-testid="add-player-firstname" />
                </div>
                <div>
                  <label className="text-[10px] text-[#666] mb-1 block">Nom *</label>
                  <Input placeholder="Nom" value={manualPlayer.last_name} onChange={(e) => setManualPlayer({ ...manualPlayer, last_name: e.target.value })} className="bg-[#252525] border-[#333] text-white h-9" data-testid="add-player-lastname" />
                </div>
                <div>
                  <label className="text-[10px] text-[#666] mb-1 block">Email</label>
                  <Input placeholder="email@exemple.com" value={manualPlayer.email} onChange={(e) => setManualPlayer({ ...manualPlayer, email: e.target.value })} className="bg-[#252525] border-[#333] text-white h-9" data-testid="add-player-email" />
                </div>
                <div>
                  <label className="text-[10px] text-[#666] mb-1 block">Téléphone</label>
                  <Input placeholder="06 00 00 00 00" value={manualPlayer.phone} onChange={(e) => setManualPlayer({ ...manualPlayer, phone: e.target.value })} className="bg-[#252525] border-[#333] text-white h-9" data-testid="add-player-phone" />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                <div className="flex-1">
                  <label className="text-[10px] text-[#666] mb-1 block">Moyen de paiement *</label>
                  <Select value={manualPlayer.payment_method} onValueChange={(v) => setManualPlayer({ ...manualPlayer, payment_method: v })}>
                    <SelectTrigger className="bg-[#252525] border-[#333] text-white h-9" data-testid="add-player-payment"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-[#333]">
                      {PAYMENT_METHODS.map((pm) => <SelectItem key={pm.value} value={pm.value} className="text-white">{pm.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleAddPlayer} disabled={actionLoading} className="bg-[#FF6B35] hover:bg-[#E55A25] gap-1 shrink-0 h-9" data-testid="add-player-submit"><UserPlus className="w-4 h-4" />Ajouter</Button>
              </div>
            </div>

            {/* Participants list */}
            <div className="max-h-80 overflow-y-auto space-y-1.5">
              {participants.map((p) => {
                const fullName = [p.first_name, p.last_name].filter(Boolean).join(' ') || p.name;
                return (
                  <div key={p.id} className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-[#252525]" data-testid={`participant-row-${p.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-medium text-sm">{fullName}</span>
                        {p.pseudo && <span className="text-[#666] text-xs">@{p.pseudo}</span>}
                        {p.is_guest && <Badge className="bg-[#333] text-[#999] text-[9px] px-1.5 py-0">Invité</Badge>}
                      </div>
                      <p className="text-[#888] text-xs mt-0.5">{[p.email, p.phone].filter(Boolean).join(' · ') || 'Aucun contact'}</p>
                    </div>
                    <Select value={p.payment_method || 'en_attente'} onValueChange={(v) => handleChangePayment(p.id, v)}>
                      <SelectTrigger className={`h-7 w-[130px] shrink-0 border-0 text-xs font-medium ${paymentBadgeClass(p.payment_method || 'en_attente')}`} data-testid={`payment-select-${p.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-[#333]">
                        {PAYMENT_METHODS.map((pm) => <SelectItem key={pm.value} value={pm.value} className="text-white">{pm.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" onClick={() => confirmRemovePlayer(p)} className="text-[#666] hover:text-red-400 hover:bg-red-400/10 h-8 w-8 shrink-0" data-testid={`remove-${p.id}`}><UserMinus className="w-4 h-4" /></Button>
                  </div>
                );
              })}
              {participants.length === 0 && <p className="text-[#666] text-center py-6 text-sm">Aucun joueur inscrit</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ REMOVE PLAYER CONFIRM ============ */}
      <Dialog open={dialogType === 'remove-player'} onOpenChange={() => { setRemoveTarget(null); setDialogType('players'); }}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle>Retirer le joueur</DialogTitle>
            <DialogDescription className="text-[#999]">
              Retirer <span className="text-white font-medium">{removeTarget?.pseudo || removeTarget?.name}</span> de {selected?.name} ?
            </DialogDescription>
          </DialogHeader>
          {selected?.price > 0 && (
            <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <CreditCard className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-amber-300 font-semibold text-sm">Ce joueur a payé {selected.price}€</p>
                <p className="text-[#999] text-xs mt-1">
                  Si le joueur a déjà réglé son inscription, il devra être remboursé du même montant ({selected.price}€) 
                  par le même moyen de paiement qu'il a utilisé pour s'inscrire.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRemoveTarget(null); setDialogType('players'); }} className="border-[#333] text-white">Annuler</Button>
            <Button onClick={handleRemovePlayer} disabled={actionLoading} className="bg-red-500 hover:bg-red-600 gap-1">
              <UserMinus className="w-4 h-4" />Retirer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ WAITLIST DIALOG ============ */}
      <Dialog open={dialogType === 'waitlist'} onOpenChange={closeDialog}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] text-white max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Liste d'attente & Intéressés — {selected?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Manual add to waitlist */}
            <div className="rounded-lg border border-[#333] bg-[#151515] p-3 space-y-3" data-testid="manual-add-waitlist-form">
              <p className="text-xs font-semibold text-[#FF6B35] uppercase tracking-wide flex items-center gap-1.5"><UserPlus className="w-3.5 h-3.5" />Ajouter à la liste d'attente</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input placeholder="Prénom *" value={manualWaitlist.first_name} onChange={(e) => setManualWaitlist({ ...manualWaitlist, first_name: e.target.value })} className="bg-[#252525] border-[#333] text-white h-9" data-testid="waitlist-firstname" />
                <Input placeholder="Nom *" value={manualWaitlist.last_name} onChange={(e) => setManualWaitlist({ ...manualWaitlist, last_name: e.target.value })} className="bg-[#252525] border-[#333] text-white h-9" data-testid="waitlist-lastname" />
                <Input placeholder="Email *" value={manualWaitlist.email} onChange={(e) => setManualWaitlist({ ...manualWaitlist, email: e.target.value })} className="bg-[#252525] border-[#333] text-white h-9" data-testid="waitlist-email" />
                <Input placeholder="Téléphone *" value={manualWaitlist.phone} onChange={(e) => setManualWaitlist({ ...manualWaitlist, phone: e.target.value })} className="bg-[#252525] border-[#333] text-white h-9" data-testid="waitlist-phone" />
              </div>
              <div className="flex justify-end">
                <Button onClick={handleAddWaitlist} disabled={actionLoading} className="bg-[#FF6B35] hover:bg-[#E55A25] gap-1 h-9" data-testid="waitlist-submit"><UserPlus className="w-4 h-4" />Ajouter & notifier</Button>
              </div>
              <p className="text-[10px] text-[#666]">La personne recevra un email (et une notification push si elle a l'application).</p>
            </div>

            {waitlistData.waitlist.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-[#FF6B35]">File d'attente ({waitlistData.waitlist.length})</h3>
                  <Button onClick={handleRelanceWaitlist} disabled={actionLoading} size="sm" className="bg-[#7B68EE] hover:bg-[#6A5ACD] gap-1.5 h-8 text-xs" data-testid="waitlist-relance-btn">
                    <Bell className="w-3.5 h-3.5" />Proposer la place au 1er
                  </Button>
                </div>
                <p className="text-[10px] text-[#666] mb-2">Envoie une offre (email + notification push) au premier de la file, valable {selected?.waitlist_confirm_minutes || 30} min.</p>
                <div className="space-y-1.5">{waitlistData.waitlist.map((u, i) => (
                  <div key={u.id} className="px-3 py-2 rounded-lg bg-[#252525] flex items-center gap-2" data-testid={`waitlist-row-${u.id}`}>
                    <span className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${i === 0 ? 'bg-[#7B68EE] text-white' : 'bg-[#333] text-[#888]'}`}>{i + 1}</span>
                    <div className="min-w-0">
                      <span className="text-white text-sm font-medium">{[u.first_name, u.last_name].filter(Boolean).join(' ') || u.pseudo || u.name}</span>
                      <p className="text-[#666] text-xs">{[u.email, u.phone].filter(Boolean).join(' · ')}</p>
                    </div>
                  </div>
                ))}</div>
              </div>
            )}
            {waitlistData.interested.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-[#7B68EE] mb-2">Intéressés ({waitlistData.interested.length})</h3>
                <div className="space-y-1.5">{waitlistData.interested.map((u) => (
                  <div key={u.id} className="px-3 py-2 rounded-lg bg-[#252525]">
                    <span className="text-white text-sm font-medium">{[u.first_name, u.last_name].filter(Boolean).join(' ') || u.pseudo || u.name}</span>
                    <p className="text-[#666] text-xs">{[u.email, u.phone].filter(Boolean).join(' · ')}</p>
                  </div>
                ))}</div>
              </div>
            )}
            {waitlistData.waitlist.length === 0 && waitlistData.interested.length === 0 && (
              <p className="text-[#666] text-center py-6 text-sm">Aucune personne en attente ou intéressée</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ EDIT DIALOG (FULL CREATION FIELDS) ============ */}
      <Dialog open={dialogType === 'edit'} onOpenChange={closeDialog}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifier le tournoi</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-[#666] mb-1 block">Nom *</label>
              <Input value={editData.name || ''} onChange={(e) => setEditData({ ...editData, name: e.target.value })} className="bg-[#252525] border-[#333] text-white" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#666] mb-1 block">Jeu *</label>
                <Select value={editData.game || ''} onValueChange={(v) => setEditData({ ...editData, game: v, format: '' })}>
                  <SelectTrigger className="bg-[#252525] border-[#333] text-white"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-[#333]">
                    {GAMES.map(g => <SelectItem key={g.id} value={g.id} className="text-white">{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#666] mb-1 block">Format *</label>
                <Select value={editData.format || ''} onValueChange={(v) => setEditData({ ...editData, format: v })}>
                  <SelectTrigger className="bg-[#252525] border-[#333] text-white"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-[#333]">
                    {editFormats.map(f => <SelectItem key={f} value={f} className="text-white">{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-[#666] mb-1 block">Description</label>
              <textarea value={editData.description || ''} onChange={(e) => setEditData({ ...editData, description: e.target.value })} rows={4} className="w-full rounded-md bg-[#252525] border border-[#333] text-white p-3 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#FF6B35]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#666] mb-1 block">Date de début *</label>
                <Input type="datetime-local" value={editData.date || ''} onChange={(e) => setEditData({ ...editData, date: e.target.value })} className="bg-[#252525] border-[#333] text-white" />
              </div>
              <div>
                <label className="text-xs text-[#666] mb-1 block">Date de fin</label>
                <Input type="datetime-local" value={editData.end_date || ''} onChange={(e) => setEditData({ ...editData, end_date: e.target.value })} className="bg-[#252525] border-[#333] text-white" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#666] mb-1 block">Date limite d'inscription</label>
              <Input type="datetime-local" value={editData.registration_deadline || ''} onChange={(e) => setEditData({ ...editData, registration_deadline: e.target.value })} className="bg-[#252525] border-[#333] text-white" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#666] mb-1 block">Prix (€)</label>
                <Input type="number" min="0" step="0.5" value={editData.price ?? 0} onChange={(e) => setEditData({ ...editData, price: parseFloat(e.target.value) || 0 })} className="bg-[#252525] border-[#333] text-white" />
              </div>
              <div>
                <label className="text-xs text-[#666] mb-1 block">Capacité (min: {currentPlayerCount})</label>
                <Input type="number" min={currentPlayerCount || 2} value={editData.capacity ?? 32} onChange={(e) => {
                  const val = parseInt(e.target.value) || 2;
                  setEditData({ ...editData, capacity: Math.max(val, currentPlayerCount || 2) });
                }} className="bg-[#252525] border-[#333] text-white" />
                {currentPlayerCount > 0 && <p className="text-[10px] text-[#666] mt-1">{currentPlayerCount} joueur(s) déjà inscrit(s)</p>}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[#666] mb-1 block">Nom du lieu</label>
                <Input value={editData.venue_name || ''} onChange={(e) => setEditData({ ...editData, venue_name: e.target.value })} className="bg-[#252525] border-[#333] text-white" placeholder="Deckbox Gisors" />
              </div>
              <div>
                <label className="text-xs text-[#666] mb-1 block">Adresse</label>
                <Input value={editData.venue_address || ''} onChange={(e) => setEditData({ ...editData, venue_address: e.target.value })} className="bg-[#252525] border-[#333] text-white" placeholder="33 rue de Vienne" />
              </div>
              <div>
                <label className="text-xs text-[#666] mb-1 block">Ville</label>
                <Input value={editData.venue_city || ''} onChange={(e) => setEditData({ ...editData, venue_city: e.target.value })} className="bg-[#252525] border-[#333] text-white" placeholder="Gisors" />
              </div>
            </div>
          </div>

          {/* ===== Bannière de l'événement ===== */}
          <div className="pt-3 mt-2 border-t border-[#333]">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-white">Bannière de l'événement</label>
              <Button type="button" variant="outline" size="sm" onClick={() => setShowBannerEditor(v => !v)} className="border-[#333] text-white gap-1.5" data-testid="edit-toggle-banner">
                <ImageIcon className="w-3.5 h-3.5" />{showBannerEditor ? 'Masquer' : (editData.banner_url ? 'Modifier la bannière' : 'Créer une bannière')}
              </Button>
            </div>
            {!showBannerEditor && editData.banner_url && (
              <img src={editData.banner_url.startsWith('http') ? editData.banner_url : `${process.env.REACT_APP_BACKEND_URL}${editData.banner_url}`} alt="Bannière" className="w-full rounded-lg border border-[#333]" data-testid="edit-current-banner" />
            )}
            {!showBannerEditor && !editData.banner_url && (
              <p className="text-xs text-[#666]">Aucune bannière pour ce tournoi. Cliquez sur « Créer une bannière » pour en générer une.</p>
            )}
            {showBannerEditor && (
              <div data-testid="edit-banner-editor">
                <BannerEditor
                  tournamentName={editData.name}
                  gameName={getGameName ? getGameName(editData.game) : editData.game}
                  gameId={editData.game}
                  dateStr={editData.date ? new Date(editData.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                  format={editData.format}
                  address={editData.venue_name || editData.venue_city}
                  shopName={user?.company_name}
                  shopLogoUrl={user?.logo_url}
                  onBannerReady={setEditBannerBlob}
                />
                <p className="text-[11px] text-[#666] mt-2">La bannière ci-dessus sera enregistrée quand vous cliquerez sur « Enregistrer ».</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} className="border-[#333] text-white">Annuler</Button>
            <Button onClick={handleUpdate} disabled={actionLoading} className="bg-[#FF6B35] hover:bg-[#E55A25]">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ CLOSE (CLOTURER) CONFIRM DIALOG ============ */}
      <Dialog open={dialogType === 'close'} onOpenChange={closeDialog}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle>Clôturer le tournoi</DialogTitle>
            <DialogDescription className="text-[#999]">
              Clôturer "<span className="text-white font-medium">{selected?.name}</span>" ?
            </DialogDescription>
          </DialogHeader>
          <div className="p-3 rounded-lg bg-[#252525] text-sm text-[#999]">
            <p>Le tournoi sera déplacé dans l'historique et ne sera plus visible dans la liste des tournois organisés.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} className="border-[#333] text-white">Annuler</Button>
            <Button onClick={handleClose} disabled={actionLoading} className="bg-green-600 hover:bg-green-700 gap-1">
              <CheckCircle className="w-4 h-4" />Clôturer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ DELETE DIALOG ============ */}
      <Dialog open={dialogType === 'delete'} onOpenChange={closeDialog}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400">Supprimer le tournoi</DialogTitle>
            <DialogDescription className="text-[#999]">Supprimer "<span className="text-white font-medium">{selected?.name}</span>" ?</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {selected?.price > 0 && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                <CreditCard className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                <p className="text-red-300">Les {(selected?.participants || []).length} joueur(s) inscrit(s) seront remboursés de {selected?.price}€ sur leur mode de paiement original.</p>
              </div>
            )}
            {participants.length > 0 && (
              <div className="p-3 rounded-lg bg-[#252525] border border-[#333]" data-testid="delete-players-list">
                <p className="text-[#FF6B35] font-medium mb-2">{participants.length} joueur(s) inscrit(s) seront notifiés (app + email) :</p>
                <div className="max-h-52 overflow-y-auto space-y-1.5">
                  {participants.map((p) => (
                    <div key={p.id} className="flex flex-col px-3 py-2 rounded bg-[#1a1a1a]">
                      <span className="text-white text-sm font-medium">{p.pseudo || p.name}</span>
                      <span className="text-[#888] text-xs">{[p.email, p.phone].filter(Boolean).join(' · ') || 'Aucun contact'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-start gap-2 p-3 rounded-lg bg-[#252525]">
              <AlertTriangle className="w-4 h-4 text-[#FF6B35] mt-0.5 shrink-0" />
              <p className="text-[#999]">Une notification (application + email) sera envoyée à tous les inscrits. Cette action est irréversible.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} className="border-[#333] text-white">Annuler</Button>
            <Button onClick={handleDelete} disabled={actionLoading} className="bg-red-500 hover:bg-red-600 gap-1"><Trash2 className="w-4 h-4" />Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pushDialog} onOpenChange={(v) => { if (!v) setPushDialog(null); }}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] text-white max-w-md" data-testid="push-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bell className="w-5 h-5 text-[#FFB800]" />Pousser une notification</DialogTitle>
            <DialogDescription className="text-[#888]">
              Prévenez les joueurs du même jeu/format situés à moins de 10 km de votre boutique.
            </DialogDescription>
          </DialogHeader>
          {!pushDialog?.quota ? (
            <div className="py-6 text-center text-[#888]">Chargement…</div>
          ) : (
            <div className="space-y-3 text-sm">
              <div className="rounded-lg bg-[#252525] p-3 space-y-1.5">
                <div className="flex justify-between"><span className="text-[#999]">Joueurs ciblés</span><span className="font-bold text-white" data-testid="push-target-count">{pushDialog.quota.target_count}</span></div>
                <div className="flex justify-between"><span className="text-[#999]">Ce mois-ci</span><span className="text-white">{pushDialog.quota.used}/{pushDialog.quota.cap} push utilisés</span></div>
                <div className="flex justify-between"><span className="text-[#999]">Statut boutique</span><span className="text-white capitalize">{pushDialog.quota.status}</span></div>
                <div className="flex justify-between"><span className="text-[#999]">Push gratuits restants</span><span className="text-[#4ECDC4] font-semibold">{pushDialog.quota.free_remaining}</span></div>
              </div>
              {pushDialog.quota.remaining <= 0 ? (
                <p className="text-amber-400 text-xs" data-testid="push-cap-reached">Plafond mensuel atteint ({pushDialog.quota.cap}/mois). Réessayez le mois prochain.</p>
              ) : pushDialog.quota.free_remaining > 0 ? (
                <p className="text-[#4ECDC4] text-xs">Ce push est <b>gratuit</b> (inclus dans votre statut).</p>
              ) : (
                <p className="text-[#FF6B35] text-xs" data-testid="push-paid-notice">Push payant : <b>{pushDialog.quota.price_eur}€</b> (redirection vers le paiement sécurisé).</p>
              )}
              {pushDialog.quota.target_count === 0 && (
                <p className="text-[#888] text-xs">Aucun joueur ne correspond pour l'instant (jeu/format + 10 km + position enregistrée).</p>
              )}
              {pushDialog.quota.recipients?.length > 0 && (
                <div>
                  <p className="text-[#999] text-xs mb-1.5">Destinataires ({pushDialog.quota.target_count})</p>
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-[#333] divide-y divide-[#222]" data-testid="push-recipients-list">
                    {pushDialog.quota.recipients.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 px-2.5 py-1.5">
                        <div className="w-6 h-6 rounded-full bg-[#252525] overflow-hidden shrink-0 flex items-center justify-center">
                          {r.avatar_url ? <img src={r.avatar_url} alt="" className="w-full h-full object-cover" /> : <span className="text-[#FF6B35] text-[10px] font-bold">{(r.pseudo || '?')[0].toUpperCase()}</span>}
                        </div>
                        <span className="text-white text-xs truncate flex-1">{r.pseudo}</span>
                        {r.city && <span className="text-[#666] text-[10px] truncate">{r.city}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPushDialog(null)} className="border-[#333] text-white" data-testid="push-cancel-btn">Annuler</Button>
            <Button
              onClick={confirmPush}
              disabled={pushLoading || !pushDialog?.quota || pushDialog.quota.remaining <= 0 || pushDialog.quota.target_count === 0}
              className="bg-[#FFB800] hover:bg-[#E5A600] text-black gap-1"
              data-testid="push-confirm-btn"
            >
              <Bell className="w-4 h-4" />
              {pushDialog?.quota && pushDialog.quota.free_remaining <= 0 && pushDialog.quota.remaining > 0 ? `Payer ${pushDialog.quota.price_eur}€ et envoyer` : 'Envoyer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
